/**
 *  ADAPTIVE CARDIO PACE & DISTANCE PROGRESSION
 *
 * Adds intelligent progression to cardio training based on:
 *
 * 1. PACE IMPROVEMENT - Are they getting faster at the same distance?
 *    - Track pace across sessions (min/mile or min/km)
 *    - Progressive improvement = fitness adaptation
 *
 * 2. DISTANCE PROGRESSION - Can they maintain pace at longer distances?
 *    - If pace is consistent, add distance
 *    - 10% rule for distance progression (max 10% increase/week)
 *
 * 3. RPE CALIBRATION - Is the effort appropriate?
 *    - Low RPE at target pace = ready to progress
 *    - High RPE = maintain or reduce
 *
 * 4. VDOT ESTIMATION - Training pace zones based on performance
 *    - Calculate VDOT from recent time trials
 *    - Provide appropriate easy/tempo/threshold paces
 *
 * PROGRESSION PATHWAYS (the two that exist):
 * - Increase distance (same pace) - endurance focus
 * - Upgrade to tempo/threshold work - lactate threshold development
 *
 * There is deliberately no "increase pace" pathway. It was advertised here and
 * carried a `suggestedPace` field for months, but nothing ever assigned either
 * one, so the switch case rendered "Push pace to undefined" — unreachable, and
 * wrong if it had been reached. Both are now gone. Prescribing a faster pace
 * needs a real target derived from VDOT zones; when that is built it should
 * arrive as new code, not as a revived stub.
 *
 * This complements adaptiveCardio.ts (recovery-based adjustments) with
 * actual performance-based progression.
 *
 * @module adaptiveCardioPace
 */

import { getSessionHistory, type ExerciseLog } from './sessionStorage';
import { getCurrentUserId } from './anonymousAuth';
import { db } from './db';
import type { PeriodizationPhase } from './periodization';
import { normalizeExerciseMatchKey } from './exerciseDatabase';
import { classifyCardioModality, supportsVDOT, cardioTypeParam, type CardioModality } from './cardioModality';

// ============================================================================
// TYPES
// ============================================================================

export type CardioProgressionPath =
  | 'increase_distance'    // Add more distance at same pace
  | 'upgrade_intensity'    // Move from easy to tempo, tempo to threshold
  | 'maintain'             // Keep current protocol
  | 'deload';              // Reduce volume/intensity

export type IntensityZone = 'easy' | 'moderate' | 'tempo' | 'threshold' | 'vo2max';

export interface CardioPerformanceMetrics {
  // Session metrics
  distance: number;           // meters
  time: number;               // seconds
  pacePerMile: number;        // seconds per mile
  pacePerKm: number;          // seconds per km

  // Effort
  rpe: number | null;

  // Ruck-specific
  ruckWeight?: number;        // lbs

  // Treadmill-specific
  incline?: number;           // 0-15% grade

  // Session info
  sessionDate: string;
  exerciseName: string;
}

export interface VDOTEstimate {
  vdot: number;               // Jack Daniels' VDOT score (VO2max proxy)
  easyPacePerMile: string;    // "9:30-10:15"
  tempoPacePerMile: string;   // "7:45"
  thresholdPacePerMile: string; // "7:15"
  intervalPacePerMile: string;  // "6:30"
  confidence: 'high' | 'medium' | 'low';
  basedOnRace?: {
    distance: number;
    time: number;
  };
}

export interface CardioProgressionDecision {
  exerciseName: string;
  /** Honest modality. Widened from the 5-way union so an incline walk or an
   * elliptical is no longer forced to report itself as a run. Echoed only —
   * VDOT and pace comparability gate on the NAME via lib/cardioModality. */
  exerciseType: CardioModality;

  // Current performance
  currentAvgPace: string;     // min/mile format
  currentDistance: number;    // meters
  currentAvgRPE: number;

  // Recommended changes
  suggestedDistance?: number;
  suggestedIntensity?: IntensityZone;

  // Decision details
  progressionPath: CardioProgressionPath;
  reason: string;
  confidence: 'high' | 'medium' | 'low';

  // Trends
  paceTrend: 'improving' | 'stable' | 'declining';
  distanceTrend: 'increasing' | 'stable' | 'decreasing';
  inclineTrend?: 'increasing' | 'stable' | 'decreasing';

  // Incline-specific (treadmill exercises)
  currentAvgIncline?: number;
  suggestedIncline?: number;
  inclineProgressionReady?: boolean;

  // VDOT (if enough data)
  vdot?: VDOTEstimate;

  // Data quality
  sessionsAnalyzed: number;
  readyForProgression: boolean;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Parse time string to seconds
 * Handles formats: "MM:SS", "H:MM:SS", "30:00", etc.
 */
function parseTimeToSeconds(timeStr: string | undefined): number {
  if (!timeStr) return 0;

  const parts = timeStr.split(':').map(Number);
  if (parts.some(n => Number.isNaN(n))) return 0;
  if (parts.length === 2) {
    // MM:SS
    return parts[0] * 60 + parts[1];
  } else if (parts.length === 3) {
    // H:MM:SS
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }

  /**
   * A BARE NUMBER IS MINUTES.
   *
   * `actualTime` is documented as MM:SS, but the cardio/swim UI has been
   * writing plain integers for a long time. Production carries "30", "20" and
   * "12" alongside "3:00" and "09:10" — and a one-part split fell through
   * both branches and returned ZERO. Measured: 18 swim/interval logs, every
   * one with no sets, no rounds and no distance, so a duration of 0 made the
   * entire session invisible to volume, training load, ACWR and calories.
   *
   * Minutes, not seconds: these are whole-bout durations for a swim or track
   * session. Reading "20" as twenty seconds would be a 20-second swim.
   */
  if (parts.length === 1) {
    const v = parts[0];
    return v > 0 ? Math.round(v * 60) : 0;
  }
  return 0;
}

/**
 * Format seconds to pace string (MM:SS/mi)
 */
function formatPace(secondsPerMile: number): string {
  // Round FIRST, then split. `Math.round(x % 60)` renders 599.7 s as "9:60"
  // because the modulo is taken before the rounding carries into the minute.
  const total = Math.round(secondsPerMile);
  const mins = Math.floor(total / 60);
  const secs = total % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}/mi`;
}

/**
 * Convert meters to miles
 */
function metersToMiles(meters: number): number {
  return meters / 1609.34;
}

/**
 * Calculate pace per mile from distance (meters) and time (seconds)
 */
function calculatePacePerMile(distanceMeters: number, timeSeconds: number): number {
  if (distanceMeters <= 0 || timeSeconds <= 0) return 0;
  const miles = metersToMiles(distanceMeters);
  return timeSeconds / miles;
}

/**
 * Calculate VDOT from a race/time trial result
 * Based on Jack Daniels' running formula
 */
function calculateVDOT(distanceMeters: number, timeSeconds: number): number {
  // Simplified VDOT calculation
  // This is an approximation of the Daniels formula
  const minutes = timeSeconds / 60;
  const km = distanceMeters / 1000;

  // Calculate velocity in meters/minute
  const velocity = distanceMeters / minutes;

  // Estimate VO2 using the running economy curve
  // VO2 = -4.60 + 0.182258 * velocity + 0.000104 * velocity^2
  const vo2 = -4.60 + 0.182258 * velocity + 0.000104 * Math.pow(velocity, 2);

  // Calculate percent of VO2max based on duration
  // %VO2max = 0.8 + 0.1894393 * e^(-0.012778 * minutes) + 0.2989558 * e^(-0.1932605 * minutes)
  const percentVO2max = 0.8 + 0.1894393 * Math.exp(-0.012778 * minutes) +
    0.2989558 * Math.exp(-0.1932605 * minutes);

  // VDOT = VO2 / %VO2max
  const vdot = vo2 / percentVO2max;

  return Math.round(vdot * 10) / 10; // Round to 1 decimal
}

/**
 * Get training paces from VDOT
 * Based on Jack Daniels' training zones
 */
function getTrainingPacesFromVDOT(vdot: number): VDOTEstimate {
  // These are approximations based on VDOT tables
  // VDOT 30: easy ~11:30, tempo ~9:30, threshold ~9:00
  // VDOT 40: easy ~9:30, tempo ~7:45, threshold ~7:15
  // VDOT 50: easy ~8:00, tempo ~6:30, threshold ~6:00
  // VDOT 60: easy ~6:45, tempo ~5:30, threshold ~5:00

  /**
   * Interpolate across the anchors above rather than applying one flat slope.
   *
   * This used `(vdot - 40) * -6` — a constant 6 s/mile per VDOT point — which
   * contradicts the very table written above it. The real relationship is
   * non-linear: easy pace moves 12 s/point between VDOT 30 and 40 (11:30 to
   * 9:30), 9 s/point between 40 and 50, and 7.5 s/point between 50 and 60. At
   * VDOT 30 the flat slope put easy pace at 10:30 against a table value of
   * 11:30 — a full minute per mile too fast for exactly the least-trained
   * runners, who can least afford it.
   */
  /**
   * Training paces derived from the SAME Daniels equation this file already
   * uses for VDOT (`vo2 = -4.60 + 0.182258v + 0.000104v²`, see calculateVDOT),
   * inverted at the published %VO2max band for each zone.
   *
   * The previous implementation was a hardcoded anchor table whose VALUES were
   * never checked against the equation above it. Inverting that equation shows
   * every anchor's "threshold" sat at 102-108% of velocity at VO2max, and
   * "interval" at 117-121% — i.e. threshold was prescribed FASTER than the
   * athlete's vVO2max, for an effort meant to last 20-40 minutes. For a VDOT
   * 24.4 runner the table returned 9:59/mi against a true threshold near
   * 12:07/mi: 128 s/mile too fast. This is rendered on the run-finish screen
   * after every qualifying run, so it was advice an athlete could act on and
   * fail inside five minutes.
   *
   * Bands (Daniels, Running Formula):
   *   Easy       59-74% VO2max   (midpoint used; the ±30s range below spans it)
   *   Threshold  86-88%          — a comfortably-hard 20-60 min effort
   *   Interval   97-100%         — 3-5 min repeats at ~vVO2max
   *
   * The old "tempo" zone is gone. In Daniels, tempo IS threshold; the table
   * carried it as a separate pace ~30 s/mi faster, which is a zone that does
   * not exist. `tempoPacePerMile` now reports the threshold pace so existing
   * consumers keep working and stop disagreeing with themselves.
   */
  const METERS_PER_MILE = 1609.34;

  /** Invert the VO2 economy curve: given a VO2 cost, return velocity in m/min. */
  const velocityForVO2 = (vo2: number): number => {
    // 0.000104v² + 0.182258v - (4.60 + vo2) = 0  →  quadratic formula
    const a = 0.000104, b = 0.182258, c = -(4.60 + vo2);
    return (-b + Math.sqrt(b * b - 4 * a * c)) / (2 * a);
  };

  /** Seconds per mile at a given fraction of VDOT. */
  const paceAtPercent = (pct: number): number => {
    const v = velocityForVO2(vdot * pct);           // m/min
    if (!(v > 0)) return 0;
    return (METERS_PER_MILE / v) * 60;             // s/mile
  };

  const adjustedPaces = {
    easy: paceAtPercent(0.665),      // midpoint of 59-74%
    tempo: paceAtPercent(0.87),      // = threshold; kept for API compatibility
    threshold: paceAtPercent(0.87),  // midpoint of 86-88%
    interval: paceAtPercent(0.985),  // midpoint of 97-100%
  };

  // Format easy pace as a range (±30 seconds)
  // Round to whole seconds BEFORE splitting, same carry bug as formatPace:
  // `Math.round(x % 60)` on 599.7 s renders "9:60".
  const mmss = (totalSeconds: number): string => {
    const t = Math.round(totalSeconds);
    return `${Math.floor(t / 60)}:${(t % 60).toString().padStart(2, '0')}`;
  };

  return {
    vdot,
    easyPacePerMile: `${mmss(adjustedPaces.easy - 30)}-${mmss(adjustedPaces.easy + 30)}`,
    tempoPacePerMile: formatPace(adjustedPaces.tempo).replace('/mi', ''),
    thresholdPacePerMile: formatPace(adjustedPaces.threshold).replace('/mi', ''),
    intervalPacePerMile: formatPace(adjustedPaces.interval).replace('/mi', ''),
    confidence: vdot >= 25 && vdot <= 70 ? 'medium' : 'low'
  };
}

/**
 * Get cardio exercise history with parsed metrics
 */
async function getCardioExerciseHistory(exerciseName: string): Promise<CardioPerformanceMetrics[]> {
  const sessions = await getSessionHistory();
  const metrics: CardioPerformanceMetrics[] = [];

  const targetMatchKey = normalizeExerciseMatchKey(exerciseName);
  const targetModality = classifyCardioModality(exerciseName);

  /**
   * Pool history WITHIN a modality — never across one.
   *
   * Keying purely on the exercise name shattered this athlete's aerobic
   * history across 32 names for 113 bouts: nine different spellings of
   * walking (Treadmill Incline Walk, Walking Treadmill, Walk, Incline Walk,
   * Cooldown Walk, Easy Walk...), four of running, four of rucking. Only 10
   * of 32 names ever reached the two comparable bouts the engine needs, so
   * 63% of decisions were made with the engine never reasoning at all — it
   * returned 'maintain' for want of data, which is indistinguishable from
   * 'maintain' as a judgement.
   *
   * Pooled by modality the same 113 bouts become a handful of real
   * histories. Crucially this does NOT merge running with walking, walking
   * with cycling, or a bike with a rower — those have genuinely different
   * relationships between distance, time and effort, and averaging them
   * describes no session anyone did.
   *
   * Unclassifiable names fall back to exact-key matching. "Easy Cardio" is
   * 33 of this athlete's logs and the modality was never recorded, so
   * pooling it into anything would be a guess.
   */
  const matchesTarget = (name: string): boolean => {
    if (normalizeExerciseMatchKey(name) === targetMatchKey) return true;
    if (targetModality === 'other') return false;
    return classifyCardioModality(name) === targetModality;
  };

  sessions.forEach(session => {
    if (session.sessionType !== 'range' && session.exercises) {
      session.exercises.forEach(ex => {
        /**
         * Match on the canonical key, not by substring.
         *
         * This was a BIDIRECTIONAL `includes`, so querying "Run" pooled every
         * log whose name merely contains those three letters — "Crunches" is
         * the one that shows up in real data, and it has no distance or pace,
         * so it silently polluted the baseline the pace engine reasons from.
         * The reverse direction is just as bad: "Run" contains "Ru", so any
         * short name swallowed longer ones.
         *
         * normalizeExerciseMatchKey is the same variant-tolerant key
         * getExerciseHistory uses — it collapses spelling variants of the same
         * movement without merging different ones.
         */
        if (matchesTarget(ex.exerciseName)) {

          // Need both distance and time to calculate pace
          const distance = ex.actualDistance;
          const time = parseTimeToSeconds(ex.actualTime);

          if (distance && distance > 0 && time > 0) {
            const pacePerMile = calculatePacePerMile(distance, time);
            // A km is shorter than a mile, so seconds-per-km is LESS than
            // seconds-per-mile — divide, don't multiply. (Was ×1.60934, which
            // inflated pace/km by ~2.6×.)
            const pacePerKm = pacePerMile / 1.60934;

            metrics.push({
              distance,
              time,
              pacePerMile,
              pacePerKm,
              rpe: ex.actualRPE ?? null,
              ruckWeight: ex.actualLoad,
              incline: ex.actualIncline,
              sessionDate: typeof session.date === 'string' ? session.date : new Date(session.date).toISOString(),
              exerciseName: ex.exerciseName
            });
          }
        }
      });
    }
  });

  // Sort by date (oldest first)
  return metrics.sort((a, b) =>
    new Date(a.sessionDate).getTime() - new Date(b.sessionDate).getTime()
  );
}

/**
 * Calculate pace trend (is the athlete getting faster?)
 */
function calculatePaceTrend(
  metrics: CardioPerformanceMetrics[],
  lookback: number = 5
): 'improving' | 'stable' | 'declining' {
  if (metrics.length < 3) return 'stable';

  const recentMetrics = metrics.slice(-lookback);
  if (recentMetrics.length < 3) return 'stable';

  // Split into first half and second half
  const midpoint = Math.floor(recentMetrics.length / 2);
  const firstHalf = recentMetrics.slice(0, midpoint);
  const secondHalf = recentMetrics.slice(midpoint);

  // Calculate average pace for each half
  const firstAvgPace = firstHalf.reduce((sum, m) => sum + m.pacePerMile, 0) / firstHalf.length;
  const secondAvgPace = secondHalf.reduce((sum, m) => sum + m.pacePerMile, 0) / secondHalf.length;

  // Pace difference (negative = faster = improving)
  const paceDiff = secondAvgPace - firstAvgPace;
  const diffPercent = (paceDiff / firstAvgPace) * 100;

  if (diffPercent < -3) return 'improving';  // >3% faster
  if (diffPercent > 3) return 'declining';   // >3% slower
  return 'stable';
}

/**
 * Calculate incline trend (is the athlete progressing to steeper inclines?)
 */
function calculateInclineTrend(
  metrics: CardioPerformanceMetrics[],
  lookback: number = 5
): 'increasing' | 'stable' | 'decreasing' | null {
  // Filter for sessions with incline data
  const inclineMetrics = metrics.filter(m => m.incline !== undefined && m.incline !== null);

  if (inclineMetrics.length < 3) return null;

  const recentMetrics = inclineMetrics.slice(-lookback);
  if (recentMetrics.length < 3) return null;

  // Split into first half and second half
  const midpoint = Math.floor(recentMetrics.length / 2);
  const firstHalf = recentMetrics.slice(0, midpoint);
  const secondHalf = recentMetrics.slice(midpoint);

  // Calculate average incline for each half
  const firstAvgIncline = firstHalf.reduce((sum, m) => sum + (m.incline || 0), 0) / firstHalf.length;
  const secondAvgIncline = secondHalf.reduce((sum, m) => sum + (m.incline || 0), 0) / secondHalf.length;

  // Incline difference (positive = increasing)
  const inclineDiff = secondAvgIncline - firstAvgIncline;

  // 1% incline change is significant
  if (inclineDiff >= 1) return 'increasing';
  if (inclineDiff <= -1) return 'decreasing';
  return 'stable';
}

/**
 * Determine if ready for incline progression
 * Criteria:
 * - Current incline < 12% (room to progress)
 * - Pace stable or improving
 * - RPE <= 7.5
 * - At least 3 sessions at current incline level
 */
function analyzeInclineProgression(
  metrics: CardioPerformanceMetrics[],
  paceTrend: 'improving' | 'stable' | 'declining',
  avgRPE: number,
  recoveryScore?: number
): {
  currentAvgIncline: number;
  suggestedIncline?: number;
  inclineProgressionReady: boolean;
  reason: string;
} {
  // Filter for sessions with incline data
  const inclineMetrics = metrics.filter(m => m.incline !== undefined && m.incline !== null);

  if (inclineMetrics.length < 2) {
    return {
      currentAvgIncline: 0,
      inclineProgressionReady: false,
      reason: 'Not enough incline data to analyze'
    };
  }

  // Get recent sessions (last 4)
  const recentIncline = inclineMetrics.slice(-4);
  const currentAvgIncline = recentIncline.reduce((sum, m) => sum + (m.incline || 0), 0) / recentIncline.length;

  // Recovery override - suggest decreasing incline if recovery is low
  if (recoveryScore !== undefined && recoveryScore < 70) {
    return {
      currentAvgIncline: Math.round(currentAvgIncline * 10) / 10,
      suggestedIncline: Math.max(0, Math.round(currentAvgIncline - 1)),
      inclineProgressionReady: false,
      reason: `Low recovery (${recoveryScore}/100). Reduce incline by 1%.`
    };
  }

  // Check if RPE is too high
  if (avgRPE > 8.5) {
    return {
      currentAvgIncline: Math.round(currentAvgIncline * 10) / 10,
      suggestedIncline: Math.max(0, Math.round(currentAvgIncline - 1)),
      inclineProgressionReady: false,
      reason: `High RPE (${avgRPE.toFixed(1)}). Consider reducing incline by 1%.`
    };
  }

  // Check if at maximum practical incline
  if (currentAvgIncline >= 12) {
    return {
      currentAvgIncline: Math.round(currentAvgIncline * 10) / 10,
      inclineProgressionReady: false,
      reason: `At high incline (${currentAvgIncline.toFixed(0)}%). Focus on pace or duration.`
    };
  }

  // Declining pace - don't increase incline
  if (paceTrend === 'declining') {
    return {
      currentAvgIncline: Math.round(currentAvgIncline * 10) / 10,
      inclineProgressionReady: false,
      reason: 'Pace declining. Maintain current incline until pace stabilizes.'
    };
  }

  // Ready for progression: stable/improving pace + manageable RPE + room to grow
  if ((paceTrend === 'stable' || paceTrend === 'improving') && avgRPE <= 7.5) {
    const suggestedIncline = Math.min(15, Math.round(currentAvgIncline) + 1);
    return {
      currentAvgIncline: Math.round(currentAvgIncline * 10) / 10,
      suggestedIncline,
      inclineProgressionReady: true,
      reason: `Pace ${paceTrend}, RPE good (${avgRPE.toFixed(1)}). Ready for ${suggestedIncline}% incline.`
    };
  }

  // Moderate RPE - maintain
  return {
    currentAvgIncline: Math.round(currentAvgIncline * 10) / 10,
    inclineProgressionReady: false,
    reason: `Building strength at ${currentAvgIncline.toFixed(0)}% incline. RPE: ${avgRPE.toFixed(1)}.`
  };
}

// ============================================================================
// CORE ADAPTIVE LOGIC
// ============================================================================

/**
 * Analyze cardio progression for a specific exercise
 */
export async function analyzeCardioProgression(
  exerciseName: string,
  exerciseType: CardioModality = 'run',
  lookbackSessions: number = 6,
  recoveryScore?: number,
  periodizationPhase?: PeriodizationPhase
): Promise<CardioProgressionDecision | null> {
  // Get history
  const history = await getCardioExerciseHistory(exerciseName);

  if (history.length < 2) {
    return {
      exerciseName,
      exerciseType,
      currentAvgPace: '--:--',
      currentDistance: 0,
      currentAvgRPE: 7,
      progressionPath: 'maintain',
      reason: `Need at least 2 cardio sessions with time data for analysis. Current: ${history.length}`,
      confidence: 'low',
      paceTrend: 'stable',
      distanceTrend: 'stable',
      sessionsAnalyzed: history.length,
      readyForProgression: false
    };
  }

  const recentHistory = history.slice(-lookbackSessions);

  /**
   * FITNESS signals pool across the modality; the DISTANCE anchor does not.
   *
   * Pooling by modality is what gives the engine enough data to reason at all
   * (see getCardioExerciseHistory). But "how fit am I at walking" and "how far
   * is THIS session" are different questions. A 400 m cooldown walk and a
   * 3.4 km incline walk are both walking, and their mean is a distance the
   * athlete has never covered — prescribing from it told a cooldown walk to
   * grow toward the average of every walk in the pool. Measured: pooling alone
   * moved the distance decisions to 49 up / 0 down, which is the old cut-only
   * defect wearing the opposite sign.
   *
   * So: pace, RPE and VDOT read the whole modality pool. Distance reads only
   * logs of THIS exercise, falling back to the pool when there are none.
   */
  // Drawn from the FULL history, not the pooled last-N window: after pooling,
  // the six most recent walks may contain no logs of this particular walk.
  const thisExerciseKey = normalizeExerciseMatchKey(exerciseName);
  const sameName = history
    .filter(m => normalizeExerciseMatchKey(m.exerciseName) === thisExerciseKey)
    .slice(-lookbackSessions);
  const distanceHistory = sameName.length > 0 ? sameName : recentHistory;

  // Calculate aggregates
  const avgPace = recentHistory.reduce((sum, m) => sum + m.pacePerMile, 0) / recentHistory.length;
  const avgDistance = distanceHistory.reduce((sum, m) => sum + m.distance, 0) / distanceHistory.length;
  const avgRPE = recentHistory.reduce((sum, m) => sum + (m.rpe || 7), 0) / recentHistory.length;

  // Calculate trends
  const paceTrend = calculatePaceTrend(recentHistory);

  // Distance trend — same-exercise only, for the reason above.
  let distanceTrend: 'increasing' | 'stable' | 'decreasing' = 'stable';
  if (distanceHistory.length >= 3) {
    const recentAvgDist = distanceHistory.slice(-2).reduce((sum, m) => sum + m.distance, 0) / 2;
    const olderAvgDist = distanceHistory.slice(0, -2).reduce((sum, m) => sum + m.distance, 0) / Math.max(1, distanceHistory.length - 2);
    const distDiff = ((recentAvgDist - olderAvgDist) / olderAvgDist) * 100;
    if (distDiff > 5) distanceTrend = 'increasing';
    else if (distDiff < -5) distanceTrend = 'decreasing';
  }

  // Incline trend (for treadmill exercises)
  const inclineTrend = calculateInclineTrend(recentHistory);
  const inclineAnalysis = analyzeInclineProgression(recentHistory, paceTrend, avgRPE, recoveryScore);

  // Calculate VDOT from best recent effort.
  //
  // Filter for eligibility FIRST, then pick the fastest of what qualifies.
  // The previous order picked the fastest bout outright and only then asked
  // whether it was long enough — so a single 400 m stride became `bestEffort`
  // and disqualified the whole athlete, silently. Backtesting ecorbin791's
  // 113 real bouts produced a VDOT on 0 of 139 decisions, which is every
  // pace anchor on the run-finish screen going dark.
  //
  // Daniels' VDOT is only defined for efforts long enough to be aerobically
  // limited; 1 mile / 5 minutes is the floor this file has always used. The
  // constraint was never wrong, it was just applied to the wrong candidate.
  const VDOT_MIN_DISTANCE_M = 1600;
  const VDOT_MIN_TIME_S = 300;
  let vdotEstimate: VDOTEstimate | undefined;

  // VDOT is a RUNNING metric — gate on the exercise NAME, not on the
  // `exerciseType` parameter. The parameter cannot be trusted:
  // WorkoutDisplayV2 passes a hardcoded 'run' for every cardio exercise, and
  // the two other call sites each rolled their own substring ternary that
  // classified "Elliptical Trainer" and "Treadmill Incline Walk" as running.
  //
  // Measured on ecorbin791: the highest-scoring effort was a "Row/Bike
  // Sprint" at VDOT 50.7 while his best real run was 31.9, so every training
  // pace was anchored ~19 VDOT points too fast.
  const vdotEligible = supportsVDOT(exerciseName)
    ? recentHistory.filter(
        m => m.distance >= VDOT_MIN_DISTANCE_M
          && m.time >= VDOT_MIN_TIME_S
          // A pool keyed by name can still contain differently-named logs via
          // alias resolution, so re-check each contributing effort.
          && supportsVDOT(m.exerciseName),
      )
    : [];
  const bestEffort = vdotEligible.length
    ? vdotEligible.reduce((best, m) => (m.pacePerMile < best.pacePerMile ? m : best), vdotEligible[0])
    : undefined;
  if (bestEffort) {
    const vdot = calculateVDOT(bestEffort.distance, bestEffort.time);
    if (vdot >= 20 && vdot <= 80) {
      vdotEstimate = {
        ...getTrainingPacesFromVDOT(vdot),
        basedOnRace: {
          distance: bestEffort.distance,
          time: bestEffort.time
        }
      };
    }
  }

  // ===== DECISION LOGIC =====
  let progressionPath: CardioProgressionPath = 'maintain';
  let reason = '';
  let confidence: 'high' | 'medium' | 'low' = 'medium';
  let suggestedDistance: number | undefined;
  let suggestedIntensity: IntensityZone | undefined;
  let readyForProgression = false;

  // ===== PERIODIZATION OVERRIDE =====
  if (periodizationPhase?.type === 'deload') {
    progressionPath = 'deload';
    suggestedDistance = Math.round(avgDistance * 0.6);
    suggestedIntensity = 'easy';
    reason = `Deload week: Reduce distance to ${Math.round(suggestedDistance / 1000)}km, easy effort only`;
    confidence = 'high';
    return {
      exerciseName,
      exerciseType,
      currentAvgPace: formatPace(avgPace),
      currentDistance: Math.round(avgDistance),
      currentAvgRPE: Math.round(avgRPE * 10) / 10,
      suggestedDistance,
      suggestedIntensity,
      progressionPath,
      reason,
      confidence,
      paceTrend,
      distanceTrend,
      vdot: vdotEstimate,
      sessionsAnalyzed: recentHistory.length,
      readyForProgression: false
    };
  }

  // ===== RECOVERY-BASED ADJUSTMENT =====
  if (recoveryScore !== undefined && recoveryScore < 70) {
    progressionPath = recoveryScore < 60 ? 'deload' : 'maintain';
    suggestedDistance = recoveryScore < 60 ? Math.round(avgDistance * 0.7) : undefined;
    suggestedIntensity = 'easy';
    reason = `Low recovery (${recoveryScore}/100). ${recoveryScore < 60 ? 'Reduce distance, easy effort.' : 'Maintain current distance at easy effort.'}`;
    confidence = 'high';
    return {
      exerciseName,
      exerciseType,
      currentAvgPace: formatPace(avgPace),
      currentDistance: Math.round(avgDistance),
      currentAvgRPE: Math.round(avgRPE * 10) / 10,
      suggestedDistance,
      suggestedIntensity,
      progressionPath,
      reason,
      confidence,
      paceTrend,
      distanceTrend,
      vdot: vdotEstimate,
      sessionsAnalyzed: recentHistory.length,
      readyForProgression: false
    };
  }

  // ===== PERFORMANCE-BASED PROGRESSION =====

  // Rule 1: Improving pace + low RPE = ready for progression
  if (paceTrend === 'improving' && avgRPE <= 7) {
    readyForProgression = true;

    if (avgRPE <= 6) {
      // Very comfortable - increase distance
      progressionPath = 'increase_distance';
      suggestedDistance = Math.round(avgDistance * 1.1); // 10% increase
      reason = `Pace improving (${formatPace(avgPace)}) at low RPE (${avgRPE.toFixed(1)}). Increase distance 10%.`;
      confidence = 'high';
    } else {
      // Moderate - small distance increase
      progressionPath = 'increase_distance';
      suggestedDistance = Math.round(avgDistance * 1.05); // 5% increase
      reason = `Pace improving at manageable RPE. Add 5% distance.`;
      confidence = 'medium';
    }
  }
  // Rule 2: Stable pace + low RPE + stable distance = ready for intensity upgrade or distance
  else if (paceTrend === 'stable' && avgRPE <= 6.5 && distanceTrend !== 'decreasing') {
    readyForProgression = true;

    if (recentHistory.length >= 4) {
      // Enough sessions - can try intensity upgrade
      progressionPath = 'upgrade_intensity';
      suggestedIntensity = 'tempo';
      reason = `Consistent performance at easy effort (RPE ${avgRPE.toFixed(1)}). Ready for tempo work.`;
      confidence = 'medium';
    } else {
      // Fewer sessions - increase distance first
      progressionPath = 'increase_distance';
      suggestedDistance = Math.round(avgDistance * 1.08);
      reason = `Stable pace at low RPE - building base with +8% distance.`;
      confidence = 'medium';
    }
  }
  // Rule 3: Stable pace + high RPE = maintain
  else if (paceTrend === 'stable' && avgRPE >= 8) {
    progressionPath = 'maintain';
    reason = `Pace stable but RPE high (${avgRPE.toFixed(1)}). Continue building fitness at current level.`;
    confidence = 'medium';
  }
  // Rule 4: Declining pace = need recovery or reduce volume
  else if (paceTrend === 'declining') {
    if (avgRPE >= 8) {
      progressionPath = 'deload';
      suggestedDistance = Math.round(avgDistance * 0.85);
      suggestedIntensity = 'easy';
      reason = `Pace declining with high RPE (${avgRPE.toFixed(1)}) - signs of overreaching. Reduce volume 15%.`;
      confidence = 'high';
    } else {
      progressionPath = 'maintain';
      suggestedIntensity = 'easy';
      reason = `Pace declining but RPE manageable. Maintain volume at easy effort.`;
      confidence = 'medium';
    }
  }
  // Default: maintain
  else {
    progressionPath = 'maintain';
    reason = `Building fitness - pace: ${formatPace(avgPace)}, RPE: ${avgRPE.toFixed(1)}. Continue current training.`;
    confidence = 'low';
  }

  return {
    exerciseName,
    exerciseType,
    currentAvgPace: formatPace(avgPace),
    currentDistance: Math.round(avgDistance),
    currentAvgRPE: Math.round(avgRPE * 10) / 10,
    suggestedDistance,
    suggestedIntensity,
    progressionPath,
    reason,
    confidence,
    paceTrend,
    distanceTrend,
    inclineTrend: inclineTrend || undefined,
    currentAvgIncline: inclineAnalysis.currentAvgIncline || undefined,
    suggestedIncline: inclineAnalysis.suggestedIncline,
    inclineProgressionReady: inclineAnalysis.inclineProgressionReady,
    vdot: vdotEstimate,
    sessionsAnalyzed: recentHistory.length,
    readyForProgression
  };
}

/**
 * Get a summary of cardio progressions for dashboard
 */
export async function getCardioProgressionSummary(): Promise<{
  hasData: boolean;
  recommendations: string[];
  vdot?: number;
  trainingPaces?: VDOTEstimate;
}> {
  const sessions = await getSessionHistory();

  // Find cardio exercises
  const cardioExercises = new Set<string>();
  sessions.forEach(session => {
    if (session.sessionType !== 'range' && session.exercises) {
      session.exercises.forEach(ex => {
        const name = ex.exerciseName.toLowerCase();
        if (name.includes('run') || name.includes('ruck') || name.includes('row') ||
            name.includes('bike') || name.includes('swim')) {
          cardioExercises.add(ex.exerciseName);
        }
      });
    }
  });

  if (cardioExercises.size === 0) {
    return {
      hasData: false,
      recommendations: ['Complete cardio sessions to enable adaptive tracking']
    };
  }

  const recommendations: string[] = [];
  let bestVDOT: VDOTEstimate | undefined;

  // Analyze each cardio exercise
  for (const exercise of Array.from(cardioExercises)) {
    // Was a substring ternary defaulting to 'swim', so "Elliptical Trainer"
    // and "Easy Cardio" were analysed as swimming. Shared classifier now.
    const exerciseType = cardioTypeParam(exercise);

    const decision = await analyzeCardioProgression(exercise, exerciseType);
    if (!decision) continue;

    if (decision.readyForProgression) {
      switch (decision.progressionPath) {
        case 'increase_distance':
          // Print the DELTA, not the new total. This read
          // `suggestedDistance / 1000` behind a "+" — so a 6.0 km bout with a
          // 6.5 km suggestion rendered "Ready for +6.5km", a ~13x
          // overstatement. An athlete acting on it doubles the session.
          const deltaM = Math.max(0, (decision.suggestedDistance || 0) - (decision.currentDistance || 0));
          recommendations.push(` ${exercise}: Ready for +${(Math.round(deltaM / 100) / 10).toFixed(1)}km`);
          break;
        case 'upgrade_intensity':
          recommendations.push(` ${exercise}: Try tempo work`);
          break;
      }
    } else if (decision.progressionPath === 'deload') {
      recommendations.push(`⬇ ${exercise}: Reduce to ${Math.round((decision.suggestedDistance || 0) / 100) / 10}km`);
    }

    // Add incline progression recommendations (for treadmill exercises)
    if (decision.inclineProgressionReady && decision.suggestedIncline !== undefined) {
      recommendations.push(` ${exercise}: Ready for ${decision.suggestedIncline}% incline (from ${decision.currentAvgIncline}%)`);
    } else if (decision.currentAvgIncline !== undefined && decision.currentAvgIncline > 0 && decision.suggestedIncline !== undefined && decision.suggestedIncline < decision.currentAvgIncline) {
      recommendations.push(` ${exercise}: Consider reducing incline to ${decision.suggestedIncline}%`);
    }

    // Track best VDOT — running only.
    //
    // This took the max across EVERY cardio exercise, and cycling/rowing
    // cover more ground per minute than running, so the winner was
    // systematically a machine effort. `trainingPaces` below is then handed
    // straight to the run screen. analyzeCardioProgression now refuses to
    // emit a vdot for a non-running modality, so `decision.vdot` is already
    // clean; the explicit check keeps that guarantee local and visible.
    if (decision.vdot && supportsVDOT(exercise) && (!bestVDOT || decision.vdot.vdot > bestVDOT.vdot)) {
      bestVDOT = decision.vdot;
    }
  }

  return {
    hasData: true,
    recommendations: recommendations.length > 0 ? recommendations : ['Cardio training on track'],
    vdot: bestVDOT?.vdot,
    trainingPaces: bestVDOT
  };
}

// ============================================================================
// RUCK-SPECIFIC PROGRESSION
// ============================================================================

/**
 * Analyze ruck-specific progression (weight + distance + pace)
 */
export async function analyzeRuckProgression(
  exerciseName: string,
  lookbackSessions: number = 5,
  recoveryScore?: number,
  periodizationPhase?: PeriodizationPhase
): Promise<{
  suggestedWeight?: number;
  suggestedDistance?: number;
  progressionPath: CardioProgressionPath;
  reason: string;
  currentWeight: number;
  currentDistance: number;
  currentPace: string;
  readyForProgression: boolean;
} | null> {
  const history = await getCardioExerciseHistory(exerciseName);
  const ruckHistory = history.filter(m => m.ruckWeight && m.ruckWeight > 0);

  if (ruckHistory.length < 2) {
    return null;
  }

  const recentHistory = ruckHistory.slice(-lookbackSessions);

  /**
   * PRESCRIPTION ANCHORS STAY WITH THIS RUCK; fitness signals may pool.
   *
   * Same split as analyzeCardioProgression, and it is if anything more
   * important here. Since cardio history began pooling by modality, all four
   * of this athlete's ruck names share one pool — and they are not one
   * session: 'Ruck March' runs 3.2-4.8 km at 25-70 lb while '6 Mile Ruck' is
   * 9.65 km. Averaging them produces a load and a distance that match no ruck
   * he has ever done, and it was BOTH the weight and the distance, which is
   * the whole prescription.
   *
   * Caught in the 2026-08-12 back-audit of that pooling change — the cardio
   * path got this treatment when the pooling landed and this path did not.
   *
   * Pace and RPE still read the pool: how fast and how hard rucking feels
   * generalises across ruck sessions in a way that load and distance do not.
   */
  const thisRuckKey = normalizeExerciseMatchKey(exerciseName);
  const sameRuck = ruckHistory
    .filter(m => normalizeExerciseMatchKey(m.exerciseName) === thisRuckKey)
    .slice(-lookbackSessions);
  const anchorHistory = sameRuck.length > 0 ? sameRuck : recentHistory;

  const avgWeight = anchorHistory.reduce((sum, m) => sum + (m.ruckWeight || 0), 0) / anchorHistory.length;
  const avgDistance = anchorHistory.reduce((sum, m) => sum + m.distance, 0) / anchorHistory.length;
  const avgPace = recentHistory.reduce((sum, m) => sum + m.pacePerMile, 0) / recentHistory.length;
  const avgRPE = recentHistory.reduce((sum, m) => sum + (m.rpe || 7), 0) / recentHistory.length;

  // Ruck progression rules:
  // 1. Low RPE + consistent pace = add weight (5 lbs)
  // 2. Moderate RPE + good pace = add distance (10%)
  // 3. High RPE = maintain
  // 4. Periodization/recovery overrides

  let progressionPath: CardioProgressionPath = 'maintain';
  let suggestedWeight: number | undefined;
  let suggestedDistance: number | undefined;
  let reason = '';
  let readyForProgression = false;

  // Periodization override
  if (periodizationPhase?.type === 'deload') {
    progressionPath = 'deload';
    suggestedWeight = Math.round(avgWeight * 0.6);
    suggestedDistance = Math.round(avgDistance * 0.7);
    reason = 'Deload week: Reduce ruck weight and distance significantly';
    return {
      suggestedWeight,
      suggestedDistance,
      progressionPath,
      reason,
      currentWeight: Math.round(avgWeight),
      currentDistance: Math.round(avgDistance),
      currentPace: formatPace(avgPace),
      readyForProgression: false
    };
  }

  // Recovery override
  if (recoveryScore !== undefined && recoveryScore < 70) {
    progressionPath = recoveryScore < 60 ? 'deload' : 'maintain';
    if (recoveryScore < 60) {
      suggestedWeight = Math.round(avgWeight * 0.8);
      suggestedDistance = Math.round(avgDistance * 0.8);
    }
    reason = `Low recovery (${recoveryScore}/100). ${recoveryScore < 60 ? 'Reduce ruck load.' : 'Maintain current ruck protocol.'}`;
    return {
      suggestedWeight,
      suggestedDistance,
      progressionPath,
      reason,
      currentWeight: Math.round(avgWeight),
      currentDistance: Math.round(avgDistance),
      currentPace: formatPace(avgPace),
      readyForProgression: false
    };
  }

  // Performance-based progression
  if (avgRPE <= 6) {
    readyForProgression = true;
    progressionPath = 'increase_distance'; // Actually increasing weight for rucks
    suggestedWeight = Math.round(avgWeight + 5);
    reason = `Low RPE (${avgRPE.toFixed(1)}) at ${Math.round(avgWeight)}lbs. Add 5lbs.`;
  } else if (avgRPE <= 7.5) {
    readyForProgression = true;
    progressionPath = 'increase_distance';
    suggestedDistance = Math.round(avgDistance * 1.1);
    reason = `Manageable RPE (${avgRPE.toFixed(1)}). Increase ruck distance 10%.`;
  } else {
    progressionPath = 'maintain';
    reason = `RPE ${avgRPE.toFixed(1)} at ${Math.round(avgWeight)}lbs. Building capacity at current load.`;
  }

  return {
    suggestedWeight,
    suggestedDistance,
    progressionPath,
    reason,
    currentWeight: Math.round(avgWeight),
    currentDistance: Math.round(avgDistance),
    currentPace: formatPace(avgPace),
    readyForProgression
  };
}

// ============================================================================
// EXPORTS
// ============================================================================

export {
  parseTimeToSeconds,
  formatPace,
  metersToMiles,
  calculatePacePerMile,
  calculateVDOT,
  getTrainingPacesFromVDOT
};
